import type { ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import type { Applicant } from "../VisaApplicantCard";

interface ApplicantTableProps {
  applicants: Applicant[];
  onRemove: (applicantId: string) => void;
  isReadOnly?: boolean;
}

function GenderAvatar({
  gender,
  name,
}: {
  gender: string;
  name: string;
}) {
  const isFemale = gender?.toLowerCase() === "female";

  return (
    <span
      title={`${name} · ${gender}`}
      aria-label={`${name}, ${gender}`}
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white text-base shadow-sm ${
        isFemale
          ? "bg-pink-100 text-pink-700"
          : "bg-blue-100 text-blue-700"
      }`}
    >
      {isFemale ? "👩" : "👨"}
    </span>
  );
}

export default function ApplicantTable({
  applicants,
  onRemove,
  isReadOnly = false,
}: ApplicantTableProps) {
  return (
    <CardWrapper>
      <div className="p-5">
        <h3 className="mb-3 text-sm font-semibold">
          Added Applicants ({applicants.length})
        </h3>

        {applicants.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
            No applicants added.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="border-b">
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Applicant
                  </TableHead>

                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    ITS
                  </TableHead>

                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Passport
                  </TableHead>

                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Nationality
                  </TableHead>

                  <TableHead className="px-4 py-3 text-sm text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {applicants.map((applicant) => {
                  const name =
                    applicant.fullName ||
                    [
                      applicant.title,
                      applicant.firstName,
                      applicant.middleName,
                      applicant.lastName,
                    ]
                      .filter(Boolean)
                      .join(" ");

                  return (
                    <TableRow
                      key={applicant.id}
                      className="border-t"
                    >
                      <TableCell className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <GenderAvatar
                            gender={applicant.gender}
                            name={name}
                          />

                          <div>
                            <div className="text-sm text-gray-900">
                              {name}
                            </div>

                            <div className="text-[10px] text-muted-foreground">
                              {applicant.age}y · {applicant.gender}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="px-4 py-2">
                        {applicant.itsNo || "-"}
                      </TableCell>

                      <TableCell className="px-4 py-2">
                        {applicant.passportNo || "-"}
                      </TableCell>

                      <TableCell className="px-4 py-2">
                        {applicant.nationality || "-"}
                      </TableCell>

                      <TableCell className="px-4 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={isReadOnly}
                          onClick={() => onRemove(applicant.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </CardWrapper>
  );
}

function CardWrapper({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border bg-white shadow-sm">
      {children}
    </div>
  );
}